import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import GoogleLoginButton from 'src/components/button/GoogleLoginButton';
import { AuthContext } from 'src/contexts';
import { useHttp, useNotification } from 'src/hooks';
import { setToLocalStorage } from 'src/utils';
import { FaEye, FaEyeSlash } from 'react-icons/fa'; // Icons import
import IconSvg from 'src/components/common/ui/IconSvg';
import PasswordStrengthIndicator from 'src/components/common/ui/PasswordStrengthIndicator';

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
}
interface PersonalDetailsForm {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}
const InputField: React.FC<{
  label: string;
  type: string;
  name: string;
  value: string;
  placeholder: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  required?: boolean;
  isPassword?: boolean; // Password ke liye extra prop
  showStrengthIndicator?: boolean; // New prop to control the indicator
}> = ({
  label,
  type,
  name,
  value,
  placeholder,
  onChange,
  error,
  required,
  isPassword,
  showStrengthIndicator,
}) => {
    const [showPassword, setShowPassword] = useState(false); // Password toggle state

    return (
      <div className="mb-4 relative">
        <label className="flex items-center justify-between text-[#4A4D4F] lg:text-lg text-base font-normal mb-2">
          <span>
            {label} {required && <span className="text-[#CC0E3C]">*</span>}
          </span>
          {showStrengthIndicator && (
            <div className="ml-2">
              <PasswordStrengthIndicator password={value} />
            </div>
          )}
        </label>
        <div className="relative">
          <input
            type={isPassword ? (showPassword ? 'text' : 'password') : type}
            name={name}
            value={value}
            placeholder={placeholder}
            onChange={onChange}
            className={`w-full px-4 py-2 border rounded-lg placeholder-grayscale-600 lg:text-base text-sm font-inter font-normal outline-none ${error ? 'border-red-500' : 'border-gray-300'
              }`}
          />
          {isPassword && (
            <span
              className="absolute inset-y-0 right-3 flex items-center cursor-pointer text-gray-500"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <IconSvg
                  icon="hidePasswordEye"
                  className="absolute inset-y-0 right-2.5 my-auto h-5 w-5 cursor-pointer"
                />
              ) : (
                <IconSvg
                  icon="eye"
                  className="absolute inset-y-0 right-2.5 my-auto h-5 w-5 cursor-pointer"
                />
              )}
            </span>
          )}
        </div>
        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
      </div>
    );
  };

const SignupForm: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { accessToken, setAccessToken } = useContext(AuthContext);
  const { sendRequest: apiRequestHandler, isLoading: isLoading, error } = useHttp();
  const notify = useNotification();
  const validateField = (name: string, value: string): string | undefined => {
    if (!value) return 'This field is required.';

    switch (name) {
      case 'email': {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Invalid email format.';
        break;
      }
      case 'password': {
        if (value.length < 8) return 'Password must be at least 8 characters long.';
        if (!/(?=.*[a-z])/.test(value))
          return 'Password must contain at least one lowercase letter.';
        if (!/(?=.*[A-Z])/.test(value))
          return 'Password must contain at least one uppercase letter.';
        if (!/(?=.*[0-9])/.test(value)) return 'Password must contain at least one number.';
        if (!/(?=.*[!@#$%^&*])/.test(value))
          return 'Password must contain at least one special character.';
        break;
      }
      case 'confirmPassword': {
        if (value !== formData.password) return 'Passwords do not match.';
        break;
      }
      case 'firstName': {
        if (!value) return 'First name is required.';
        break;
      }
      case 'lastName': {
        if (value && value.length < 2) return 'Last name should be at least 2 characters long.';
        break;
      }
    }

    return undefined;
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Validate on change
    setErrors({ ...errors, [name]: validateField(name, value) });
  };

  const isFormValid = () =>
    Object.values(formData).every(value => value) && Object.values(errors).every(error => !error);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if the form is valid
    if (!isFormValid()) {
      notify('Please fill all fields correctly.', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
      };

      // Signup function call
      signup(payload);

      notify('Signup successful!', 'success');

      navigate('/signin');
    } catch (error) {
      notify('An error occurred during signup. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const signup = (payload: PersonalDetailsForm) => {
    apiRequestHandler(
      {
        method: 'POST',
        url: `auth/signup`,
        accessToken,
        payload: {
          email: payload.email,
          password: payload.password,
          firstName: payload.firstName,
          lastName: payload.lastName,
          // priceId: finalData?.planId,
          // currencyId: finalData?.currencyId,
        },
      },
      (data: any) => {
        setToLocalStorage(`::@@adlyJwtAuthspajs@@::`, data); //todo need to change this
        setAccessToken(data.accessToken);
        // setFinalData((prev: any) => ({ ...prev, ...payload }));
      },
    );
  };

  useEffect(() => {
    if (error) {
      notify(error || '', 'error');
    }
  }, [error]);
  return (
    <div className="lg:w-1/2 w-full lg:p-8 flex flex-col items-start">

      <IconSvg icon="appLogo" className="cursor-pointer lg:w-auto w-24" onClick={() => navigate('/')} alt="Logo" />

      <p className="lg:text-[36px] text-[28px] font-semibold text-black font-inter mt-8 lg:mb-6 mb-4">
        Sign up
      </p>
      <p className="text-[#4A4D4F] lg:text-xl text-base font-normal font-inter mb-6">
        Start Your Journey with Us – It All Begins Here!
      </p>
      <form className="w-full" onSubmit={handleSubmit}>
        <div className="flex lg:flex-row flex-col lg:items-center lg:space-x-4 mb-2">
          <div className="flex-1">
            <InputField
              label="First Name"
              type="text"
              name="firstName"
              value={formData.firstName}
              placeholder="Enter first name"
              onChange={handleChange}
              error={errors.firstName}
              required
            />
          </div>
          <div className="flex-1">
            <InputField
              label="Last Name"
              type="text"
              name="lastName"
              value={formData.lastName}
              placeholder="Enter last name"
              onChange={handleChange}
              error={errors.lastName}
              required
            />
          </div>
        </div>

        <InputField
          label="Email"
          type="email"
          name="email"
          value={formData.email}
          placeholder="Enter email"
          onChange={handleChange}
          error={errors.email}
          required
        />
        <InputField
          label="Password"
          type="password"
          name="password"
          value={formData.password}
          placeholder="Enter password"
          onChange={handleChange}
          error={errors.password}
          required
          isPassword={true}
          showStrengthIndicator={true} // Enable the strength indicator
        />

        <InputField
          label="Confirm Password"
          type="password"
          name="confirmPassword"
          value={formData.confirmPassword}
          placeholder="Confirm password"
          onChange={handleChange}
          error={errors.confirmPassword}
          required
          isPassword={true}
        />
        <button
          type="submit"
          disabled={!isFormValid() || isSubmitting}
          className={`w-full py-2 rounded text-white ${isSubmitting || !isFormValid()
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-blue-500 hover:bg-blue-600'
            }`}
        >
          {isSubmitting ? 'Signing up...' : 'Sign up'}
        </button>
      </form>
      <div className="mt-4 flex items-center w-full">
        <div className="flex-1 h-px border-t border-[#00000029]"></div>
        <div className="mx-2 text-gray-500">OR</div>
        <div className="flex-1 h-px border-t border-[#00000029]"></div>
      </div>

      <GoogleLoginButton
        label={'Continue with Google'}
        type={'signup'}
      // planId={planId}
      // currencyId={currencyId}
      />

      <div className="mt-4 flex items-center justify-center space-x-2 text-black w-full text-center lg:text-lg text-sm font-semibold">
        <p>Already have an account? </p>
        <p
          onClick={() => navigate('/signin')}
          className="cursor-pointer underline underline-offset-4 text-[#6277FF]"
        >
          Login
        </p>
      </div>
    </div>
  );
};

export default SignupForm;
